import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CanDirective, CapabilityNames } from 'core';
import { LoadingSpinner, TableColumnInfo, TableView, TypeOfColumn } from 'design-system';
import { finalize } from 'rxjs';
import { Deposit, DepositSearch, DepositStatus } from '../../interfaces/deposit.model';
import { DepositsService } from '../../services/deposits.service';
import { formatDateLabel, toDate } from '../../utils/receipt.utils';

interface DepositRow {
  id: string;
  numeroDistinta: number;
  dataCreazione: Date | null;
  importoTotale: number;
  situazione: string;
}

const STATUS_LABELS: Record<string, string> = {
  [DepositStatus.Lavorazione]: 'Lavorazione',
  [DepositStatus.Verificata]: 'Verificata',
  [DepositStatus.Chiusa]: 'Chiusa',
};

@Component({
  selector: 'rev-receipts-distinte',
  imports: [
    CanDirective,
    CurrencyPipe,
    DatePipe,
    FormsModule,
    LoadingSpinner,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatSelectModule,
    NgClass,
    RouterLink,
    TableView,
  ],
  templateUrl: './receipts-distinte.html',
  styleUrl: './receipts-distinte.scss',
})
export class ReceiptsDistinte implements OnInit {
  private readonly service = inject(DepositsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly CAPS = CapabilityNames;

  distinte: DepositRow[] = [];
  distinteTable: (Omit<DepositRow, 'dataCreazione'> & { dataCreazione: string })[] = [];
  isLoading = false;
  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;

  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  selectedStatuses: DepositStatus[] = [];

  readonly statusOptions = [
    { value: DepositStatus.Lavorazione, label: STATUS_LABELS[DepositStatus.Lavorazione] },
    { value: DepositStatus.Verificata, label: STATUS_LABELS[DepositStatus.Verificata] },
    { value: DepositStatus.Chiusa, label: STATUS_LABELS[DepositStatus.Chiusa] },
  ];

  readonly distinteColumns: TableColumnInfo[] = [
    { columnNameDef: 'numeroDistinta', label: 'N. distinta', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataCreazione', label: 'Data creazione', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'importoTotale',
      label: 'Importo totale',
      type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'situazione',
      label: 'Stato',
      type: TypeOfColumn.TEXT,
      cellClass: row => this.statusBadgeClass(row.situazione),
    },
  ];

  statusBadgeClass(status: string): string {
    switch (status) {
      case STATUS_LABELS[DepositStatus.Lavorazione]:
        return 'deposit-status-warning';
      case STATUS_LABELS[DepositStatus.Verificata]:
        return 'deposit-status-success';
      case STATUS_LABELS[DepositStatus.Chiusa]:
        return 'deposit-status-info';
      default:
        return '';
    }
  }

  ngOnInit(): void {
    this.loadDeposits();
  }

  loadDeposits(): void {
    this.isLoading = true;

    const filter: DepositSearch = {
      searchObject: {
        dataDa: this.dateFrom ? this.dateFrom.toISOString() : null,
        dataA: this.dateTo ? this.dateTo.toISOString() : null,
        statiDistinta: this.selectedStatuses,
      },
    };

    this.service
      .getDepositList(this.pageIndex + 1, this.pageSize, filter)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => {
          this.distinte = (data.items ?? []).map(deposit => this.toRow(deposit));
          this.distinteTable = this.distinte.map(distinta => ({
            ...distinta,
            dataCreazione: formatDateLabel(distinta.dataCreazione),
          }));
          this.totalItems = data.totalItems ?? 0;
        },
        error: err => {
          console.error('Errore caricamento distinte di versamento', err);
          this.distinte = [];
          this.distinteTable = [];
          this.totalItems = 0;
        },
      });
  }

  cerca(): void {
    this.pageIndex = 0;
    this.loadDeposits();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadDeposits();
  }

  apriDettaglio(idDistinta: string): void {
    this.router.navigate([idDistinta], { relativeTo: this.route });
  }

  private toRow(deposit: Deposit): DepositRow {
    return {
      id: deposit.id ?? '',
      numeroDistinta: deposit.numeroDistintaVersamenti,
      dataCreazione: toDate(deposit.data),
      importoTotale: deposit.totaleVersamento ?? 0,
      situazione: STATUS_LABELS[deposit.stato] ?? deposit.stato,
    };
  }
}
